import * as z from 'zod';

const idLike = z.union([z.string(), z.number()]);
const numLike = z.coerce.number();

/** The order a transaction belongs to (links into the order detail page). */
const transactionOrderRefSchema = z
  .object({
    id: idLike.nullish(),
    display_id: idLike.nullish(),
  })
  .loose();

/** The foul behind a fine transaction (type 9). */
const transactionFoulRefSchema = z
  .object({
    id: idLike.nullish(),
    reason: z.string().nullish(),
    total_late_minutes: numLike.nullish(),
    missed_order_count: numLike.nullish(),
    given_by_user_name: z.string().nullish(),
  })
  .loose();

/**
 * One row of `GET admin/transactions`. Loose: the backend carries more
 * columns than the table reads, and a new field must not break the page.
 */
export const transactionSchema = z
  .object({
    id: idLike,
    display_id: idLike.nullish(),
    reason: z.string().nullish(),
    amount: numLike.nullish(),
    bonus: numLike.nullish(),
    prev_balance: numLike.nullish(),
    after_balance: numLike.nullish(),
    type: numLike.nullish(),
    status: numLike.nullish(),
    phone: z.string().nullish(),
    created_at: z.string().nullish(),
    performed_at: z.string().nullish(),
    order: transactionOrderRefSchema.nullish(),
    foul: transactionFoulRefSchema.nullish(),
    courier_id: idLike.nullish(),
    courier_first_name: z.string().nullish(),
    courier_last_name: z.string().nullish(),
    vendor_id: idLike.nullish(),
    vendor_name: z.string().nullish(),
    client_id: idLike.nullish(),
    client_first_name: z.string().nullish(),
    client_last_name: z.string().nullish(),
  })
  .loose();

/** The summary strip above the table (all optional: the strip reads `0`). */
export const transactionStatsSchema = z
  .object({
    total: numLike.nullish(),
    avg: numLike.nullish(),
    min: numLike.nullish(),
    max: numLike.nullish(),
  })
  .loose();

export const transactionListSchema = z.object({
  data: z.array(transactionSchema).default([]),
  count: numLike.nullish(),
  stats: transactionStatsSchema.nullish(),
});

export type Transaction = z.infer<typeof transactionSchema>;
export type TransactionStats = z.infer<typeof transactionStatsSchema>;
export type TransactionList = z.infer<typeof transactionListSchema>;

/** A row of one of the filter-option lists (clients, bonuses, fouls). */
export const clientOptionSchema = z
  .object({
    id: idLike,
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .loose();

export const bonusOptionSchema = z
  .object({
    id: idLike,
    amount: numLike.nullish(),
    from_time: z.string().nullish(),
    till_time: z.string().nullish(),
  })
  .loose();

export const foulOptionSchema = z
  .object({
    id: idLike,
    amount: numLike.nullish(),
    reason_ru: z.string().nullish(),
    reason_uz: z.string().nullish(),
    reason_en: z.string().nullish(),
  })
  .loose();

export type ClientOption = z.infer<typeof clientOptionSchema>;
export type BonusOption = z.infer<typeof bonusOptionSchema>;
export type FoulOption = z.infer<typeof foulOptionSchema>;

/** One page of an option list plus the server total. */
export interface OptionsPage<T> {
  data: T[];
  count: number;
}
